import { faClipboardList } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSearchParamsUpdater } from 'hooks/useSearchParamsUpdater';
import { useForm } from 'hooks/useForm';
import { getBalanceHistory } from '@redux/features/clientsSlice';
import { adjustBalance } from '@redux/features/clientsSlice';
import { balanceTransactionsTypesTranslations } from 'constants/balanceTransactionTypes';
import { balanceTransactionSchema } from 'constants/formValidationSchemas/balanceSchema';
import { TabSelector } from 'components/TabSelector';
import { NoResults } from 'components/NoResults';
import { Button } from 'components/Button';
import { Input } from 'components/Input';
import { BalanceTransactionCard } from './BalanceTransactionCard';
import styles from './styles.scss';

const defaultValues = {
  type: Object.keys(balanceTransactionsTypesTranslations)[0],
  description: '',
  amount: '',
};

export const BalanceTab = () => {
  const { balanceHistory } = useSelector((state) => state.clients);
  const { locations } = useSelector((state) => state.locations);
  const [resetCounter, setResetCounter] = useState(0);
  const { clientID, locationName } = useParams();
  const query = useSearchParamsUpdater();
  const dispatch = useDispatch();

  const locationTransliteration = query.getParam('location') || locationName;

  const locationID = useMemo(
    () => locations.find((l) => l.transliteration === locationTransliteration)?.id,
    [locationTransliteration]
  );

  useEffect(() => {
    dispatch(getBalanceHistory(clientID));
  }, [dispatch, clientID]);

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, resetForm, setFieldValue } =
    useForm({
      initialValues: defaultValues,
      validationSchema: balanceTransactionSchema,
      onSubmit: async (values) => {
        const result = await dispatch(
          adjustBalance({
            description: values.description,
            type: values.type,
            amount: values.amount,
            clientID,
            locationID,
          })
        );

        if (result.meta.requestStatus === 'fulfilled') {
          dispatch(getBalanceHistory(clientID));
          setResetCounter((prev) => prev + 1);
          resetForm();
        }
      },
    });

  const handleTypeChange = (index) => {
    const typeKey = Object.keys(balanceTransactionsTypesTranslations)[index];
    setFieldValue('type', typeKey);
  };

  const clientBalanceHistory = useMemo(
    () => balanceHistory.filter((record) => record.clientID === Number(clientID)),
    [balanceHistory]
  );

  return (
    <div className={styles.tab}>
      <div className={styles.section}>
        <h2>Коригувати баланс</h2>
        <form className={styles.form} onSubmit={handleSubmit}>
          <TabSelector
            tabs={Object.values(balanceTransactionsTypesTranslations)}
            onTabChange={handleTypeChange}
            resetTrigger={resetCounter}
          />
          <Input
            placeholder='Сума'
            name='amount'
            type='number'
            value={values.amount}
            onChange={handleChange}
            onBlur={handleBlur}
            invalid={touched.amount && errors.amount}
            errorText={touched.amount && errors.amount}
          />
          <Input
            placeholder='Опис (необовʼязково)'
            name='description'
            value={values.description}
            onChange={handleChange}
            onBlur={handleBlur}
            invalid={touched.description && errors.description}
            errorText={touched.description && errors.description}
          />
          <div className={styles.buttons}>
            <Button
              text='Скинути'
              type='reset'
              onClick={() => {
                resetForm();
                setResetCounter((prev) => prev + 1);
              }}
              ghostStyle
            />
            <Button text='Створити' type='submit' disabled={isSubmitting} />
          </div>
        </form>
      </div>
      {clientBalanceHistory.length > 0 ? (
        <div className={styles.balanceAudit}>
          <span className={styles.title}>
            <FontAwesomeIcon className={styles.auditIcon} icon={faClipboardList} />
            Аудит балансу
          </span>
          <ul className={styles.balanceHistory}>
            {clientBalanceHistory.map((record) => (
              <BalanceTransactionCard record={record} key={record.id} />
            ))}
          </ul>
        </div>
      ) : (
        <NoResults text='Змін балансу ще не було' />
      )}
    </div>
  );
};
